const EMR_STATE_LABELS = {
  STARTING: 'Starting',
  BOOTSTRAPPING: 'Bootstrapping',
  RUNNING: 'Running',
  WAITING: 'Waiting',
  TERMINATING: 'Terminating',
  TERMINATED: 'Terminated',
  TERMINATED_WITH_ERRORS: 'Terminated with errors',
};

const EMR_STATUS_CODE_MESSAGES = {
  USER_REQUEST: 'User request',
  VALIDATION_ERROR: 'Validation error',
  ALL_STEPS_COMPLETED: 'All steps completed',
  STEP_FAILURE: 'Step failure',
  AUTO_TERMINATED: 'Auto-terminate',
};

const ACTIVE_EMR_STATES = new Set([
  'STARTING',
  'BOOTSTRAPPING',
  'RUNNING',
  'WAITING',
  'TERMINATING',
]);

export const getEmrStateLabel = (state) =>
  EMR_STATE_LABELS[String(state || '').toUpperCase()] ||
  String(state || 'Unknown').replace(/_/g, ' ');

export const getEmrStatusSubtext = (pipeline = {}) => {
  const message = String(pipeline.status_message || '').trim();
  if (message) return message;

  const code = String(pipeline.status_code || '').trim().toUpperCase();
  if (code && EMR_STATUS_CODE_MESSAGES[code]) {
    return EMR_STATUS_CODE_MESSAGES[code];
  }

  const state = String(pipeline.status || '').toUpperCase();
  if (state === 'WAITING') return 'Ready to run steps';
  if (state === 'RUNNING') return 'Running steps';
  return '';
};

export const getEmrStatusVisual = (state) => {
  const normalized = String(state || '').toUpperCase();

  if (normalized === 'WAITING' || normalized === 'RUNNING') {
    return { tone: 'success', icon: 'check' };
  }
  if (normalized === 'TERMINATED_WITH_ERRORS') {
    return { tone: 'error', icon: 'close' };
  }
  if (normalized === 'TERMINATED') {
    return { tone: 'default', icon: 'minus' };
  }
  if (ACTIVE_EMR_STATES.has(normalized)) {
    return { tone: 'processing', icon: 'sync' };
  }
  return { tone: 'default', icon: 'minus' };
};

export const formatEmrDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatEmrElapsedTime = (pipeline = {}) => {
  const created = pipeline.created_on ? new Date(pipeline.created_on) : null;
  if (!created || Number.isNaN(created.getTime())) return '—';

  const end = pipeline.end_on ? new Date(pipeline.end_on) : null;
  const state = String(pipeline.status || '').toUpperCase();
  const endPoint =
    end && !Number.isNaN(end.getTime())
      ? end
      : ACTIVE_EMR_STATES.has(state)
        ? new Date()
        : null;

  if (!endPoint) return '—';

  let diffSeconds = Math.max(
    0,
    Math.floor((endPoint.getTime() - created.getTime()) / 1000),
  );

  const days = Math.floor(diffSeconds / 86400);
  diffSeconds -= days * 86400;
  const hours = Math.floor(diffSeconds / 3600);
  diffSeconds -= hours * 3600;
  const minutes = Math.floor(diffSeconds / 60);
  const seconds = diffSeconds - minutes * 60;

  const parts = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  if (parts.length === 0) {
    parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
  }

  return parts.slice(0, 2).join(', ');
};

export const isEmrPipeline = (pipeline) =>
  String(pipeline?.type || '').toLowerCase() === 'emr' ||
  String(pipeline?.pipeline_id || '').startsWith('emr_');
